import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Alert, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Checkbox, Grid,
} from '@mui/material';
import { Add, Edit, Delete, Home, Security, WarningAmber, AutoFixHigh } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MODULES = [
  { key: 'finance',       label: 'Finance' },
  { key: 'academics',     label: 'Academics' },
  { key: 'hr',            label: 'HR' },
  { key: 'payroll',       label: 'Payroll' },
  { key: 'supply-chain',  label: 'Supply Chain' },
  { key: 'setups',        label: 'Setups' },
  { key: 'reports',       label: 'Reports' },
];

const ACTIONS = [
  { key: 'canView',   label: 'View' },
  { key: 'canCreate', label: 'Create' },
  { key: 'canEdit',   label: 'Edit' },
  { key: 'canDelete', label: 'Delete' },
  { key: 'canPost',   label: 'Post' },
];

const emptyPermissions = () =>
  MODULES.map(m => ({ module: m.key, canView: false, canCreate: false, canEdit: false, canDelete: false, canPost: false }));

const countGranted = (permissions = []) =>
  permissions.reduce((n, p) => n + ACTIONS.filter(a => p[a.key]).length, 0);

const emptyForm = { code: '', description: '', permissions: emptyPermissions() };

const PermissionSetList = () => {
  const navigate = useNavigate();
  const [sets,      setSets]      = useState([]);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [dlgOpen,   setDlgOpen]   = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dlgOpen, value: form, onClose: () => setDlgOpen(false) });
  const [saving,    setSaving]    = useState(false);
  const [confirmDlg,setConfirmDlg]= useState(null);
  const [seeding,   setSeeding]   = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/permission-sets`, { headers: hdrs() });
      setSets(res.data);
    } catch { setError('Failed to load permission sets'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setError(''); setDlgOpen(true);
  };

  const openEdit = (ps) => {
    setEditing(ps);
    const perms = MODULES.map(m => {
      const found = (ps.permissions || []).find(p => p.module === m.key) || {};
      return { module: m.key, canView: !!found.canView, canCreate: !!found.canCreate,
               canEdit: !!found.canEdit, canDelete: !!found.canDelete, canPost: !!found.canPost };
    });
    setForm({ code: ps.code, description: ps.description || '', permissions: perms });
    setError(''); setDlgOpen(true);
  };

  const togglePerm = (modKey, action) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.map(p =>
        p.module === modKey ? { ...p, [action]: !p[action] } : p
      ),
    }));
  };

  const toggleAll = (modKey) => {
    setForm(f => {
      const p = f.permissions.find(p => p.module === modKey);
      const allOn = ACTIONS.every(a => p[a.key]);
      return {
        ...f,
        permissions: f.permissions.map(mp =>
          mp.module === modKey
            ? { ...mp, canView: !allOn, canCreate: !allOn, canEdit: !allOn, canDelete: !allOn, canPost: !allOn }
            : mp
        ),
      };
    });
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Code is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/permission-sets/${editing.code}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/permission-sets`, form, { headers: hdrs() });
      }
      setDlgOpen(false); load();
      setSuccess(editing ? 'Permission Set updated' : 'Permission Set created');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true); setError('');
    try {
      const [psRes, ugRes] = await Promise.all([
        axios.post(`${API}/api/permission-sets/seed`, {}, { headers: hdrs() }),
        axios.post(`${API}/api/user-groups/seed`,     {}, { headers: hdrs() }),
      ]);
      load();
      setSuccess(
        `Seeded ${psRes.data.inserted} permission set(s) and ${ugRes.data.inserted} user group(s). ` +
        `Total: ${psRes.data.total} sets, ${ugRes.data.total} groups.`
      );
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.response?.data?.error || 'Seed failed');
    } finally { setSeeding(false); }
  };

  const handleDelete = async () => {
    const ps = confirmDlg; setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/permission-sets/${ps.code}`, { headers: hdrs() });
      load(); setSuccess('Deleted'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Security color="primary" />
              <Typography variant="h5" color="primary">Permission Sets</Typography>
              <Chip label={sets.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Setups">
                <IconButton onClick={() => navigate('/setups')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Tooltip title="Seed 14 default permission sets + 14 user groups (skips existing)">
                <Button variant="outlined" startIcon={<AutoFixHigh />} onClick={handleSeedDefaults} disabled={seeding}>
                  {seeding ? 'Seeding…' : 'Seed Defaults'}
                </Button>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Modules Granted</strong></TableCell>
                <TableCell><strong>Total Permissions</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>No permission sets yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : sets.map(ps => {
                const granted = countGranted(ps.permissions);
                const modulesWithAny = (ps.permissions || []).filter(p => ACTIONS.some(a => p[a.key]));
                return (
                  <TableRow key={ps._id} hover>
                    <TableCell><strong>{ps.code}</strong></TableCell>
                    <TableCell>{ps.description}</TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {modulesWithAny.map(p => (
                          <Chip key={p.module} label={MODULES.find(m => m.key === p.module)?.label || p.module}
                            size="small" variant="outlined" color="info" />
                        ))}
                        {modulesWithAny.length === 0 && <Typography variant="caption" color="text.secondary">None</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={granted} size="small" color={granted > 0 ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(ps)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setConfirmDlg(ps)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirm Delete */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete permission set <strong>{confirmDlg?.code}</strong>? Any user groups or users referencing it will lose these permissions.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      {discardDialog}
      <Dialog open={dlgOpen} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'New Permission Set'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Code *" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                fullWidth size="small" disabled={!!editing} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Permission matrix */}
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Module Permissions</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ minWidth: 120 }}><strong>Module</strong></TableCell>
                  <TableCell align="center" sx={{ minWidth: 52 }}><strong>All</strong></TableCell>
                  {ACTIONS.map(a => (
                    <TableCell key={a.key} align="center" sx={{ minWidth: 64 }}>
                      <strong>{a.label}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {MODULES.map(m => {
                  const perm = form.permissions.find(p => p.module === m.key) || {};
                  const allOn = ACTIONS.every(a => perm[a.key]);
                  return (
                    <TableRow key={m.key} hover>
                      <TableCell>{m.label}</TableCell>
                      <TableCell align="center">
                        <Checkbox size="small" checked={allOn} indeterminate={!allOn && ACTIONS.some(a => perm[a.key])}
                          onChange={() => toggleAll(m.key)} />
                      </TableCell>
                      {ACTIONS.map(a => (
                        <TableCell key={a.key} align="center">
                          <Checkbox size="small" checked={!!perm[a.key]}
                            onChange={() => togglePerm(m.key, a.key)} />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionSetList;
